import { type AxiosError, type AxiosInstance, createAxiosClientWithAladinBearer } from '@b2ag/axios'
import { useAuthService } from '@b2ag/auth'
import { buildCallerId } from '@b2ag/utils/src/caller-id'
import type { ApproBffSDK, CartAppro } from '@invivodf/module-appro-bff-sdk'
import { CartError, CartErrorKeys, CartErrorReasons } from '@invivodf/module-cart-b2ag-interfaces'
import { createBffSdk } from '@b2ag/utils/src/esmParser'
import { i18n } from '@b2ag/locale'
export type CartServiceParams = { cartApiHost: string }
export { CartAppro }

class CartService {
  private readonly cartApiHost: string
  private readonly api: AxiosInstance
  private readonly sdk: ApproBffSDK

  constructor({ cartApiHost }: CartServiceParams) {
    const accessTokenSource = async () => useAuthService().getAccessToken()

    this.cartApiHost = cartApiHost
    this.api = createAxiosClientWithAladinBearer(`${this.cartApiHost}/v1/bff/appro`, {
      callerId: buildCallerId(),
      i18n,
    })
    this.sdk = createBffSdk(cartApiHost, { callerId: buildCallerId(), accessTokenSource })
  }

  async createCart({
    membership_number,
    cooperative_id,
    product_id,
    quantity,
    variant,
    offer_id,
    discount_id,
    pre_shippings,
    payment_due_date_reference,
    services,
  }) {
    const response = await this.api.post('/carts', {
      membership_number,
      cooperative_id,
      line_items: [
        {
          product_id,
          quantity,
          ean_code: variant.ean_code,
          offer_id,
          discount_id,
          payment_due_date_reference,
          services,
        },
      ],
      pre_shippings,
    })
    return response.data
  }

  async get(cartId) {
    const response = await this.api.get(`/carts/${cartId}`)
    return response.data
  }

  async addProductToCart(
    cartId,
    productId,
    quantity,
    variant,
    offer_id,
    discount_id,
    pre_shipping,
    payment_due_date_reference,
    services,
  ) {
    const response = await this.api.post(`/carts/${cartId}/line-items`, {
      product_id: productId,
      quantity,
      ean_code: variant.ean_code,
      offer_id,
      discount_id,
      pre_shipping,
      payment_due_date_reference,
      services,
    })
    return response.data
  }

  async destroyLineItem(cartId, lineItemId) {
    const response = await this.api.delete(`/carts/${cartId}/line-items/${lineItemId}`)
    return response.data
  }

  async deleteCart(cartId) {
    const response = await this.api.delete(`/carts/${cartId}`)
    return response.data
  }

  async index(membershipNumber, cooperativeId) {
    const response = await this.api.get('/carts', {
      params: { membership_number: membershipNumber, cooperative_id: cooperativeId },
    })
    return response.data
  }

  async last(membershipNumber, cooperativeId) {
    const list = await this.index(membershipNumber, cooperativeId)
    if (!list || list.length === 0) {
      return null
    }
    return list[0]
  }

  async remakeCheckoutGetForSafari(fullURL) {
    const fetchOptions: RequestInit = {
      mode: 'cors',
      cache: 'default',
    }

    const auth = useAuthService()
    const token = await auth.getAccessToken()
    if (token) {
      fetchOptions.headers = { authorization: `Bearer ${token}` }
    }

    const cartRequest = new Request(fullURL, { ...fetchOptions, method: 'POST' })
    const cartResponse = await fetch(cartRequest)
    const checkoutURL = cartResponse.url
    const checkoutRequest = new Request(checkoutURL, { ...fetchOptions, method: 'GET' })
    const checkoutResponse = await fetch(checkoutRequest)
    return checkoutResponse.json()
  }

  async postCheckout(cartId) {
    try {
      const response = await this.api.post(`/carts/${cartId}/checkouts`)
      return response.data
    } catch (error: any) {
      if ((<AxiosError>error).response?.status === 401) {
        const fullUrl = `${this.cartApiHost}/v1/bff/appro/carts/${cartId}/checkouts`

        return this.remakeCheckoutGetForSafari(fullUrl)
      }

      const errorMessagesToMap = (error.response?.data?.errors || []).map(this.mapCartErrors)

      throw errorMessagesToMap.length
        ? {
            ...error,
            response: {
              ...error.response,
              data: {
                ...error.response?.data,
                errors: errorMessagesToMap,
              },
            },
          }
        : error
    }
  }

  async createCheckout({ id: cartId, checkout_id: checkoutId }) {
    if (!checkoutId) return this.postCheckout(cartId)
    try {
      const response = await this.api.get(`/checkouts/${checkoutId}`)
      return response.data
    } catch (error) {
      if ((<AxiosError>error).response?.status === 404) return this.postCheckout(cartId)
      throw error
    }
  }

  async addPromoCode(cartId: string, promoCode: string) {
    return this.sdk.addPromoCodeToCart({ cartId, promoCode })
  }

  async removePromoCode(cartId: string, promoCode: string) {
    return this.sdk.removePromoCodeFromCart({ cartId, promoCode })
  }

  async addLineItemsFromLogisticOrderToCart(logisticOrderId: string, membershipNumber: string, cooperativeId: number) {
    return this.sdk.addLineItemsFromLogisticOrderToCart({ logisticOrderId, membershipNumber, cooperativeId })
  }

  mapCartErrors(currentError: CartError): { message: string; name?: string } {
    switch (currentError.name) {
      case CartErrorKeys.LineItemMissingError:
        return {
          message: i18n._(`L'offre pour "{variantName}" n'est plus disponible.`, {
            variantName: currentError.data?.variantName,
          }),
          name: currentError.name,
        }
      case CartErrorKeys.LegalCertificateMissingError:
        if (currentError.data?.reason === CartErrorReasons.PHYTO) {
          return {
            message: i18n._(`Le certificat certiphyto est nécessaire pour acheter le produit "{variantName}".`, {
              variantName: currentError.data?.variantName,
            }),
            name: currentError.name,
          }
        }
        return {
          message: i18n._(
            `Le certificat précurseur d'explosifs est nécessaire pour acheter le produit "{variantName}".`,
            { variantName: currentError.data?.variantName },
          ),
          name: currentError.name,
        }
      case CartErrorKeys.OfferMissingPaymentDueDate:
        return {
          message: i18n._(`L'offre "{variantName}" ne propose plus d'échéance de paiement.`, {
            variantName: currentError.data?.variantName,
          }),
          name: currentError.name,
        }
      case CartErrorKeys.PromoCodeNotAppliedError:
        return {
          message: i18n._(`Le code promo n'est plus applicable.`),
          name: currentError.name,
        }
      case CartErrorKeys.QuotaLowerThanQuantityError:
        return {
          message: i18n._(
            `La quantité précédemment sélectionnée pour l'article "{variantName}" n'est plus disponible en quantité suffisante.`,
            { variantName: currentError.data?.variantName },
          ),
          name: currentError.name,
        }
      case CartErrorKeys.OfferDescriptionChangedError:
        return {
          message: i18n._(`La description du produit "{variantName}" a changé.`, {
            variantName: currentError.data?.variantName,
          }),
          name: currentError.name,
        }
      case CartErrorKeys.OfferPriceTypeChangedError:
        return {
          message: i18n._(`Le type de prix du produit "{variantName}" a changé.`, {
            variantName: currentError.data?.variantName,
          }),
          name: currentError.name,
        }
      case CartErrorKeys.OfferPriceChangedError:
        return {
          message: i18n._(
            `Le prix unitaire de l'offre du produit "{variantName}" a changé de {oldValue}€ à {newValue}€.`,
            {
              variantName: currentError.data?.variantName,
              oldValue: currentError.data?.oldValue,
              newValue: currentError.data?.newValue,
            },
          ),
          name: currentError.name,
        }
      case CartErrorKeys.OfferMissingService:
        return {
          message: i18n._(`L'offre sur "{variantName}" ne propose plus le service choisi.`, {
            variantName: currentError.data?.variantName,
          }),
          name: currentError.name,
        }
      case CartErrorKeys.DiscountNewDiscountError:
        return {
          message: i18n._(`Les informations de la remise de l'offre "{variantName}" ont été mise à jour.`, {
            variantName: currentError.data?.variantName,
          }),
          name: currentError.name,
        }
      case CartErrorKeys.DiscountDeletedError:
        return {
          message: i18n._(`La remise sur l'offre "{variantName}" n'est plus disponible.`, {
            variantName: currentError.data?.variantName,
          }),
          name: currentError.name,
        }
      case CartErrorKeys.OfferHasNewPaymentDueDate:
        return {
          message: i18n._(`L'offre "{variantName}" possède une nouvelle échéance de paiement "{newValue}".`, {
            variantName: currentError.data?.variantName,
            newValue: currentError.data?.newValue,
          }),
          name: currentError.name,
        }
      case CartErrorKeys.OfferDeliveryPeriodChangedError:
        return {
          message: i18n._(`Les dates de livraison pour le produit "{variantName}" ont changé.`, {
            variantName: currentError.data?.variantName,
          }),
          name: currentError.name,
        }
      case CartErrorKeys.OfferExcludedFeesChangedError:
        return {
          message: i18n._(`La valeur des taxes pour le produit "{variantName}" a changé.`, {
            variantName: currentError.data?.variantName,
          }),
          name: currentError.name,
        }
      default:
        return {
          message: i18n._(`Une erreur s'est produite. Veuillez réessayer et contacter le support si nécessaire.`),
          name: currentError.name,
        }
    }
  }
}

export default CartService
